import { Button, PageHeader, Descriptions, Card, Table, Spin, Tag, Image } from 'antd'
import { useParams } from 'react-router-dom'
import { useQuery } from 'react-query'

import Error from '../../components/Error'
import { getProductStockById } from '../../network/ProductStock'
import { getProductStockHistory } from '../../network/ProductStockHistory'
import { createUrlWithFiltersFromCurrentURL } from '../../utils/filterUtils'

const baseUrl = process.env.REACT_APP_API_URL || ''

/** Normalize name (string or { en, zh }) to a single display string */
const nameLabel = (name) =>
  typeof name === 'string' ? name : (name?.en || name?.zh || '')

const ProductStockHistory = ({ history }) => {
  const { id } = useParams()

  const { data: productStock, isError, isLoading, refetch } = useQuery(
    ['getProductStockById', { id }],
    getProductStockById,
    { refetchOnWindowFocus: false, enabled: !!id }
  )

  const { data: historyData, isLoading: historyLoading } = useQuery(
    [
      'getProductStockHistory',
      {
        sort: ['createdAt', 'DESC'],
        range: [0, 199],
        filter: { fixedSkuStock: id },
      },
    ],
    getProductStockHistory,
    { refetchOnWindowFocus: false, enabled: !!id }
  )

  const historyList = historyData?.list || []

  if (isError) {
    return (
      <>
        <PageHeader
          className="site-page-header"
          title="Stock history"
          onBack={() => history.push(createUrlWithFiltersFromCurrentURL('/fixed-sku-stock'))}
        />
        <Error retry={refetch} />
      </>
    )
  }

  if (isLoading || !productStock) {
    return (
      <>
        <PageHeader
          className="site-page-header"
          title="Stock history"
          onBack={() => history.push(createUrlWithFiltersFromCurrentURL('/fixed-sku-stock'))}
        />
        <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
          <Spin size="large" />
        </div>
      </>
    )
  }

  const fixedSku = productStock.fixedSku || {}
  const store = productStock.store || {}
  const storeName = nameLabel(store.name)

  const actionColors = {
    Purchase: 'green',
    Sale: 'blue',
    Return: 'orange',
    Adjustment: 'purple',
  }

  /** Format amounts: each size shows +X added, −Y removed (API uses add / removed) */
  const renderAmounts = (amounts) => {
    if (!Array.isArray(amounts) || amounts.length === 0) return '–'
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {amounts.map((a, idx) => {
          const label = a.size_name_en || a.size_name_zh || '?'
          const add = Number(a.add) || 0
          const removed = Number(a.removed) != null ? Number(a.removed) : Number(a.remove) || 0
          const hasAdd = add > 0
          const hasRemoved = removed > 0
          return (
            <div key={idx} style={{ fontSize: 12, lineHeight: 1.4 }}>
              <strong>{label}:</strong>{' '}
              {!hasAdd && !hasRemoved && '–'}
              {hasAdd && <span style={{ color: '#389e0d' }}>+{add} added</span>}
              {hasAdd && hasRemoved && ' '}
              {hasRemoved && <span style={{ color: '#cf1322' }}>−{removed} removed</span>}
            </div>
          )
        })}
      </div>
    )
  }

  const historyColumns = [
    {
      title: 'Date',
      key: 'createdAt',
      dataIndex: 'createdAt',
      width: 165,
      render: (v) => (v ? new Date(v).toLocaleString() : '–'),
      sorter: (a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0),
    },
    {
      title: 'Action',
      key: 'action',
      dataIndex: 'action',
      width: 115,
      render: (action) => (
        <Tag color={actionColors[action] || 'default'}>{action || '–'}</Tag>
      ),
    },
    {
      title: 'Added / Removed by size',
      key: 'amounts',
      dataIndex: 'amounts',
      width: 260,
      render: renderAmounts,
    },
    {
      title: 'User',
      key: 'user',
      width: 140,
      render: (_, record) =>
        nameLabel(record.user?.name) || record.user?.email || '–',
    },
    {
      title: 'Comment',
      key: 'comment',
      dataIndex: 'comment',
      ellipsis: true,
      width: 160,
      render: (v) => (v ? <span title={v}>{v}</span> : '–'),
    },
    {
      title: 'Images',
      key: 'images',
      dataIndex: 'images',
      width: 90,
      render: (images) => {
        if (!Array.isArray(images) || images.length === 0) return '–'
        const imgSrc = (img) => {
          const uri = img?.uri
          if (!uri) return img?.url
          return uri.startsWith('http') ? uri : `${baseUrl}/storage/fixed-sku-stock-history/${uri}`
        }
        return (
          <Image.PreviewGroup>
            {images.slice(0, 3).map((img, idx) => (
              <Image
                key={idx}
                width={36}
                height={36}
                style={{ objectFit: 'cover', marginRight: 4, borderRadius: 4 }}
                src={imgSrc(img)}
                alt=""
              />
            ))}
            {images.length > 3 && <span style={{ fontSize: 11 }}>+{images.length - 3}</span>}
          </Image.PreviewGroup>
        )
      },
    },
  ]

  return (
    <>
      <PageHeader
        className="site-page-header"
        title="Stock history"
        onBack={() => history.push(createUrlWithFiltersFromCurrentURL('/fixed-sku-stock'))}
        style={{ backgroundColor: '#fff' }}
        extra={[
          <Button
            key="view"
            onClick={() => history.push(createUrlWithFiltersFromCurrentURL(`/fixed-sku-stock/view/${id}`))}
          >
            View
          </Button>,
          <Button
            key="manage"
            type="primary"
            onClick={() => history.push(createUrlWithFiltersFromCurrentURL(`/fixed-sku-stock/manage/${id}`))}
          >
            Manage stock
          </Button>,
        ]}
      />
      <div style={{ padding: 24, backgroundColor: '#f0f2f5', minHeight: 'calc(100vh - 64px)' }}>
        <div style={{ margin: '0 auto' }}>
          <Card title="Product stock" style={{ marginBottom: 24 }}>
            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label="Fix SKU">{fixedSku.sku ?? '–'}</Descriptions.Item>
              <Descriptions.Item label="Fix SKU name">
                {fixedSku.name?.en ?? fixedSku.name?.zh ?? '–'}
              </Descriptions.Item>
              <Descriptions.Item label="Store">{storeName}</Descriptions.Item>
            </Descriptions>
          </Card>
          <Card title="History">
            {historyLoading ? (
              <div style={{ textAlign: 'center', padding: 24 }}>
                <Spin />
              </div>
            ) : historyList.length === 0 ? (
              <div style={{ color: '#999' }}>No history entries</div>
            ) : (
              <Table
                dataSource={historyList.map((row, idx) => ({ ...row, key: row.id ?? idx }))}
                columns={historyColumns}
                pagination={{ pageSize: 20, showSizeChanger: true }}
                size="small"
              />
            )}
          </Card>
        </div>
      </div>
    </>
  )
}

export default ProductStockHistory
